import { faCircleUser, faGear, faLocationDot, faRightFromBracket } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useDispatch, useSelector } from 'react-redux';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import cn from 'classnames';
import SessionStorageUtils from 'utils/SessionStorageUtils';
import { logoutUser } from '@redux/features/authSlice';
import { SessionStorageKeys } from 'constants/sessionStorageKeys';
import { Settings } from 'common/Settings';
import styles from 'common/Sidebar/Sidebar.scss';

export const Account = () => {
  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);
  const [isUserActionsOpen, setIsUserActionsOpen] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const location = SessionStorageUtils.getData(SessionStorageKeys.LOCATION);
  const { user } = useSelector((state) => state.auth);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const accountRef = useRef();

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (accountRef.current && !accountRef.current.contains(event.target)) {
        setIsUserActionsOpen(false);
      }
    };

    if (isUserActionsOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isUserActionsOpen]);

  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setIsUserActionsOpen(false);
      }
    };

    if (isUserActionsOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => document.removeEventListener('keydown', handleEscape);
  }, [isUserActionsOpen]);

  const toggleUserActions = useCallback(() => {
    setIsUserActionsOpen((prev) => !prev);
  }, []);

  const navigateToProfile = useCallback(() => {
    navigate('profile');
    setIsUserActionsOpen(false);
  }, [navigate]);

  const changeLocation = useCallback(() => {
    SessionStorageUtils.removeData(SessionStorageKeys.LOCATION);
    navigate('/locations/choose');
    setIsUserActionsOpen(false);
  }, [navigate]);

  const openSettings = useCallback(() => {
    setIsSettingsModalOpen(true);
    setIsUserActionsOpen(false);
  }, []);

  const closeSettings = useCallback(() => {
    setIsSettingsModalOpen(false);
  }, []);

  const logout = useCallback(async () => {
    if (isLoggingOut) return;

    setIsLoggingOut(true);
    setIsUserActionsOpen(false);

    try {
      await dispatch(logoutUser()).unwrap();
    } catch (error) {
      console.error('Logout error:', error);
      setIsLoggingOut(false);
    }
  }, [dispatch, isLoggingOut]);

  const userInitials = user?.name?.slice(0, 1)?.toUpperCase() || '?';

  const fullName = user ? `${user.surname} ${user.name}` : '';

  const canChangeLocation = user?.role === 3 || user?.locations?.length > 1;

  if (!user) {
    return null;
  }

  return (
    <>
      <div className={styles.accountWrapper} ref={accountRef}>
        <span
          className={styles.account}
          onClick={toggleUserActions}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              toggleUserActions();
            }
          }}
          tabIndex={0}
          role='button'
          aria-expanded={isUserActionsOpen}
          aria-haspopup='true'
        >
          {userInitials}
        </span>

        <div className={cn(styles.accountActions, { [styles.active]: isUserActionsOpen })} role='menu'>
          <div className={styles.info}>
            <span className={styles.name}>{fullName}</span>
            <span className={styles.email}>{user.email}</span>
            {location?.name && <span className={styles.location}>{location.name}</span>}
          </div>
          <button
            className={styles.accountBtn}
            tabIndex={isUserActionsOpen ? 0 : -1}
            onClick={navigateToProfile}
            role='menuitem'
          >
            <FontAwesomeIcon className={cn(styles.icon, styles.profile)} icon={faCircleUser} />
            Мій профіль
          </button>

          {canChangeLocation && (
            <button
              className={styles.accountBtn}
              tabIndex={isUserActionsOpen ? 0 : -1}
              onClick={changeLocation}
              role='menuitem'
            >
              <FontAwesomeIcon className={cn(styles.icon, styles.mark)} icon={faLocationDot} />
              Змінити локацію
            </button>
          )}
          <button
            className={styles.accountBtn}
            tabIndex={isUserActionsOpen ? 0 : -1}
            onClick={openSettings}
            role='menuitem'
          >
            <FontAwesomeIcon className={cn(styles.icon, styles.gear)} icon={faGear} />
            Налаштування
          </button>
          <button
            className={cn(styles.accountBtn, styles.exitBtn)}
            tabIndex={isUserActionsOpen ? 0 : -1}
            onClick={logout}
            disabled={isLoggingOut}
            role='menuitem'
          >
            <FontAwesomeIcon className={cn(styles.icon, styles.exit)} icon={faRightFromBracket} />
            {isLoggingOut ? 'Виходимо...' : 'Вихід'}
          </button>
        </div>
      </div>

      <Settings isOpen={isSettingsModalOpen} setIsOpen={closeSettings} />
    </>
  );
};
